/**
 * Admin API for access groups.
 *
 * The backend accepts *page keys*, never endpoint paths — the mapping from a page
 * to the endpoints it needs lives in code and is tested there. That is why this
 * client has no notion of a path pattern, and why `available_page_keys` is served
 * rather than duplicated here.
 */
import { del, get, patch, post } from './client';

export type AccessGroup = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  allowed_page_keys: string[];
  member_count: number;
};

export type AccessGroupList = {
  groups: AccessGroup[];
  /** Page keys the backend catalog defines. The UI renders one checkbox each. */
  available_page_keys: string[];
};

export type MemberAccess = {
  user_id: string;
  access_group_id: string | null;
};

export function listAccessGroups(teamId: string): Promise<AccessGroupList> {
  return get<AccessGroupList>(`/api/teams/${teamId}/access-groups`);
}

export function createAccessGroup(
  teamId: string,
  body: { name: string; description?: string | null; allowed_page_keys: string[] },
): Promise<AccessGroup> {
  return post<AccessGroup>(`/api/teams/${teamId}/access-groups`, body);
}

export function updateAccessGroup(
  teamId: string,
  groupId: string,
  body: { name?: string; description?: string | null; allowed_page_keys?: string[] },
): Promise<AccessGroup> {
  return patch<AccessGroup>(`/api/teams/${teamId}/access-groups/${groupId}`, body);
}

export function deleteAccessGroup(teamId: string, groupId: string): Promise<void> {
  return del<void>(`/api/teams/${teamId}/access-groups/${groupId}`);
}

/** Pass null for full access. */
export function setMemberAccessGroup(
  teamId: string,
  userId: string,
  accessGroupId: string | null,
): Promise<MemberAccess> {
  return patch<MemberAccess>(`/api/teams/${teamId}/members/${userId}/access-group`, {
    access_group_id: accessGroupId,
  });
}
